import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../components/AppIcon';
import { dashboardFinancieroCorpService } from '../../services/financialService';

const PERIODS = ['Mes Actual', 'Trimestre', 'Semestre', 'Año', 'Ejercicio Fiscal', 'Personalizado'];
const PERIOD_MAP = {
  'Mes Actual': 'month',
  'Trimestre': 'quarter',
  'Semestre': 'semester',
  'Año': 'year',
  'Ejercicio Fiscal': 'year',
  'Personalizado': 'custom',
};
const DEPTS = ['Consolidado', 'Sala de Corte', 'Distribución', 'Tienda General', 'Corporativo'];
const DEPT_MAP = {
  'Consolidado': 'Todos',
  'Sala de Corte': 'Sala Corte',
  'Distribución': 'Distribución',
  'Tienda General': 'Tienda',
  'Corporativo': 'Corporativo',
};
const VIEWS = ['Cuenta de Resultados', 'Centros de Coste', 'Comparativa'];

const fmt = (n) => (typeof n === 'number' ? n?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00');
const fmtEur = (n) => `${fmt(n)} €`;
const fmtPct = (n) => `${(typeof n === 'number' ? n : 0)?.toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

const reports = [
  { label: 'Cuenta de Resultados Consolidada', icon: 'FileBarChart', color: '#D6EF4E' },
  { label: 'Cuenta de Resultados por Departamento', icon: 'LayoutGrid', color: '#60A5FA' },
  { label: 'Gastos por Naturaleza', icon: 'List', color: '#F87171' },
  { label: 'Gastos por Centro de Coste', icon: 'PieChart', color: '#D6EF4E' },
  { label: 'Evolución Mensual', icon: 'LineChart', color: '#60A5FA' },
];

// ─── INDICADOR DE COBERTURA ───────────────────────────────────────────────────
const CoberturaIndicador = ({ data }) => {
  if (!data) return null;
  const { coberturaPct, componentesCobertura } = data;
  const color = coberturaPct >= 80 ? '#34D399' : coberturaPct >= 50 ? '#FBBF24' : '#F87171';

  return (
    <div className="rounded-2xl p-4"
      style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon name="ShieldCheck" size={14} style={{ color }} />
          <h3 className="font-oranienbaum font-semibold text-white/70 text-sm">Cobertura de Datos</h3>
        </div>
        <span className="font-oranienbaum font-bold text-lg" style={{ color }}>{coberturaPct}%</span>
      </div>
      {/* Barra de progreso */}
      <div className="w-full h-1.5 rounded-full mb-3" style={{ background: 'rgba(255,255,255,0.08)' }}>
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${coberturaPct}%`, background: color }} />
      </div>
      <div className="space-y-1.5">
        {(componentesCobertura || [])?.map((comp, i) => (
          <div key={i} className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span style={{ color: comp?.estado === 'COMPLETO' ? '#34D399' : '#FBBF24' }}>
                {comp?.estado === 'COMPLETO' ? '✓' : '⚠'}
              </span>
              <span className="font-oranienbaum text-[10px]" style={{ color: comp?.estado === 'COMPLETO' ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.35)' }}>
                {comp?.nombre}
              </span>
            </div>
            {comp?.estado !== 'COMPLETO' && comp?.pendienteMsg && (
              <span className="font-oranienbaum text-[9px] text-[#FBBF24]/60">{comp?.pendienteMsg}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── SECCIÓN P&L ─────────────────────────────────────────────────────────────
const PLSection = ({ title, icon, color, items, total, totalPct, isResult, loading }) => (
  <div className={`rounded-2xl overflow-hidden ${isResult ? 'ring-1' : ''}`}
    style={{
      background: isResult ? `${color}08` : 'rgba(255,255,255,0.025)',
      border: `1px solid ${isResult ? color + '30' : 'rgba(255,255,255,0.07)'}`,
    }}>
    <div className="flex items-center justify-between px-4 py-3"
      style={{ borderBottom: items?.length > 0 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
      <div className="flex items-center gap-2">
        <Icon name={icon} size={14} style={{ color }} />
        <span className="font-oranienbaum text-xs uppercase tracking-wider font-semibold"
          style={{ color: isResult ? color : 'rgba(255,255,255,0.60)' }}>
          {title}
        </span>
      </div>
      <div className="flex items-center gap-3">
        {totalPct !== undefined && (
          <span className="font-oranienbaum text-[10px]" style={{ color: 'rgba(255,255,255,0.30)' }}>
            {fmtPct(totalPct)}
          </span>
        )}
        <span className="font-oranienbaum font-bold text-sm"
          style={{ color: isResult ? color : 'rgba(255,255,255,0.70)' }}>
          {loading ? '...' : total}
        </span>
      </div>
    </div>
    {items?.length > 0 && (
      <div className="px-4 py-2 space-y-1">
        {items?.map((item, j) => (
          <div key={j} className="flex items-center justify-between py-1.5 border-b border-white/4 last:border-0">
            <span className="font-oranienbaum text-white/45 text-xs">{item?.label}</span>
            <div className="flex items-center gap-3">
              {item?.sub && (
                <span className="font-oranienbaum text-[10px] px-1.5 py-0.5 rounded"
                  style={{ color: '#FBBF24', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.15)' }}>
                  {item?.sub}
                </span>
              )}
              <span className="font-oranienbaum text-white/55 text-xs font-semibold w-28 text-right"
                style={item?.highlight ? { color: item?.highlight } : item?.isPending ? { color: '#FBBF24' } : {}}>
                {loading ? '...' : item?.value}
              </span>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const FinContabilidad = () => {
  const [period, setPeriod] = useState('Mes Actual');
  const [dept, setDept] = useState('Consolidado');
  const [view, setView] = useState('Cuenta de Resultados');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [data, setData] = useState(null);
  const [centrosData, setCentrosData] = useState([]);
  const [comparativaData, setComparativaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const periodKey = PERIOD_MAP?.[period] || 'month';
      const deptMapped = DEPT_MAP?.[dept] || 'Todos';

      const params = {
        period: periodKey,
        dateFrom: period === 'Personalizado' ? dateFrom : undefined,
        dateTo: period === 'Personalizado' ? dateTo : undefined,
        departamento: deptMapped,
      };

      if (view === 'Cuenta de Resultados') {
        const result = await dashboardFinancieroCorpService?.getContabilidadAnalitica(params);
        setData(result);
      } else if (view === 'Centros de Coste') {
        const result = await dashboardFinancieroCorpService?.getCentrosCosteDetalle(params);
        setCentrosData(result || []);
      } else if (view === 'Comparativa') {
        // Comparativa: cargar periodo actual y anterior con el mismo motor
        const [resultActual, resultAnterior] = await Promise.all([
          dashboardFinancieroCorpService?.getContabilidadAnalitica(params),
          dashboardFinancieroCorpService?.getContabilidadAnalitica({
            ...params,
            period: 'custom',
            dateFrom: getPeriodoAnteriorFrom(periodKey, period === 'Personalizado' ? dateFrom : undefined),
            dateTo: getPeriodoAnteriorTo(periodKey, period === 'Personalizado' ? dateTo : undefined),
          }),
        ]);
        setComparativaData({ actual: resultActual, anterior: resultAnterior });
        setData(resultActual);
      }
    } catch (err) {
      setError(err?.message || 'Error al cargar contabilidad');
    } finally {
      setLoading(false);
    }
  }, [period, dept, view, dateFrom, dateTo]);

  useEffect(() => { loadData(); }, [loadData]);

  // Calcular periodo anterior para comparativa
  const getPeriodoAnteriorFrom = (periodKey, customFrom) => {
    const now = new Date();
    const pad = (n) => String(n)?.padStart(2, '0');
    const ymd = (d) => `${d?.getFullYear()}-${pad(d?.getMonth() + 1)}-${pad(d?.getDate())}`;
    if (periodKey === 'month') {
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return ymd(prev);
    }
    if (periodKey === 'quarter') {
      const q = Math.floor(now?.getMonth() / 3);
      return ymd(new Date(now.getFullYear(), (q - 1) * 3, 1));
    }
    if (periodKey === 'semester') {
      return ymd(new Date(now.getFullYear(), now.getMonth() - 11, 1));
    }
    if (periodKey === 'year') {
      return `${now?.getFullYear() - 1}-01-01`;
    }
    if (customFrom) {
      const d = new Date(customFrom);
      d?.setMonth(d?.getMonth() - 1);
      return ymd(d);
    }
    return ymd(new Date(now.getFullYear(), now.getMonth() - 1, 1));
  };

  const getPeriodoAnteriorTo = (periodKey, customTo) => {
    const now = new Date();
    const pad = (n) => String(n)?.padStart(2, '0');
    const ymd = (d) => `${d?.getFullYear()}-${pad(d?.getMonth() + 1)}-${pad(d?.getDate())}`;
    if (periodKey === 'month') {
      return ymd(new Date(now.getFullYear(), now.getMonth(), 0));
    }
    if (periodKey === 'quarter') {
      const q = Math.floor(now?.getMonth() / 3);
      return ymd(new Date(now.getFullYear(), q * 3, 0));
    }
    if (periodKey === 'semester') {
      return ymd(new Date(now.getFullYear(), now.getMonth() - 6, 0));
    }
    if (periodKey === 'year') {
      return `${now?.getFullYear() - 1}-12-31`;
    }
    if (customTo) {
      const d = new Date(customTo);
      d?.setMonth(d?.getMonth() - 1);
      return ymd(d);
    }
    return ymd(new Date(now.getFullYear(), now.getMonth(), 0));
  };

  // Construir secciones P&L desde datos reales
  const buildPLSections = () => {
    if (!data) return [];
    const d = data;
    const deptMapped = DEPT_MAP?.[dept] || 'Todos';
    const deptDb = deptMapped === 'Todos' ? null : deptMapped === 'Sala Corte' ? 'SALA_CORTE' : deptMapped === 'Distribución' ? 'DISTRIBUCION' : deptMapped === 'Tienda' ? 'TIENDA' : deptMapped === 'Corporativo' ? 'CORPORATIVO' : null;

    // Helper para mostrar valor o "Pendiente de datos"
    const valOrPending = (val, isPending, pendingMsg = 'Pendiente de datos') => {
      if (isPending) return { value: pendingMsg, isPending: true };
      return { value: fmtEur(val || 0), isPending: false };
    };

    // ── INGRESOS ──
    const ingresosItems = [];

    // Sala de Corte: mostrar si aplica al dept seleccionado
    if (!deptDb || deptDb === 'SALA_CORTE') {
      ingresosItems?.push(
        d?.salaConDatos
          ? { label: 'Ventas Sala de Corte', value: fmtEur(d?.ventasSala || 0) }
          : { label: 'Ventas Sala de Corte', value: 'Pendiente de datos', isPending: true }
      );
    }

    // Tienda: solo si aplica al dept seleccionado
    if (!deptDb || deptDb === 'TIENDA') {
      ingresosItems?.push(
        d?.tiendaPendiente
          ? { label: 'Ventas Tienda General', value: 'Pendiente Epelsa', isPending: true, sub: '⚠ Epelsa' }
          : { label: 'Ventas Tienda General', value: fmtEur(d?.ventasTienda || 0) }
      );
    }

    // Distribución: solo si aplica al dept seleccionado
    if (!deptDb || deptDb === 'DISTRIBUCION') {
      ingresosItems?.push(
        d?.distribucionPendiente
          ? { label: 'Ventas Distribución', value: 'Pendiente Contasimple', isPending: true, sub: '⚠ Contasimple' }
          : { label: 'Ventas Distribución', value: fmtEur(d?.ventasDistribucion || 0) }
      );
    }

    // Otros ingresos (corporativo u otros)
    if (d?.otrosIngresos > 0) {
      ingresosItems?.push({ label: 'Otros Ingresos', value: fmtEur(d?.otrosIngresos || 0) });
    }

    // ── COGS ──
    const cogsItems = [];

    if (!deptDb || deptDb === 'SALA_CORTE') {
      cogsItems?.push(
        d?.cogsSalaPendiente
          ? { label: 'COGS Sala / Materia Prima', value: 'Pendiente de datos', isPending: true }
          : { label: 'COGS Sala / Materia Prima', value: fmtEur(d?.cogsSala || 0) }
      );
    }

    if (!deptDb || deptDb === 'TIENDA') {
      cogsItems?.push(
        d?.cogsTiendaPendiente
          ? { label: 'COGS Tienda', value: 'Pendiente Epelsa', isPending: true, sub: '⚠ Epelsa' }
          : { label: 'COGS Tienda', value: fmtEur(d?.cogsTienda || 0) }
      );
    }

    if (!deptDb || deptDb === 'DISTRIBUCION') {
      cogsItems?.push(
        d?.cogsDistribucionPendiente
          ? { label: 'COGS Distribución', value: 'Pendiente Contasimple', isPending: true, sub: '⚠ Contasimple' }
          : { label: 'COGS Distribución', value: fmtEur(0) }
      );
    }

    // Corporativo no tiene COGS directo
    if (deptDb === 'CORPORATIVO') {
      cogsItems?.push({ label: 'COGS Corporativo', value: '0,00 €' });
    }

    // Margen bruto: si hay COGS pendiente con ingresos, indicar que es parcial
    const margenBrutoTotal = d?.margenBrutoPendiente
      ? 'Parcial (COGS pendiente)'
      : fmtEur(d?.margenBruto || 0);
    const margenBrutoPct = d?.margenBrutoPendiente ? undefined : d?.margenBrutoPct;

    // ── PERSONAL ──
    const personalItems = [];

    // Salarios Sala: solo para SALA_CORTE y CONSOLIDADO
    if (!deptDb || deptDb === 'SALA_CORTE') {
      personalItems?.push({
        label: 'Salarios Sala de Corte',
        value: d?.salariosSala > 0
          ? fmtEur(d?.salariosSala)
          : (d?.salariosSalaFuente === 'REAL' ? '0,00 €' : 'Pendiente de datos'),
        isPending: d?.salariosSala === 0 && d?.salariosSalaFuente !== 'REAL',
      });
    }

    if (d?.ssEmpresa > 0) {
      personalItems?.push({ label: 'Seguridad Social', value: fmtEur(d?.ssEmpresa) });
    }
    if (d?.otroPersonal > 0) {
      personalItems?.push({ label: 'Otro Personal', value: fmtEur(d?.otroPersonal) });
    }

    return [
      {
        title: 'INGRESOS',
        color: '#34D399',
        icon: 'TrendingUp',
        items: ingresosItems,
        total: fmtEur(d?.totalIngresos || 0),
      },
      {
        title: 'COSTE DE MERCANCÍA VENDIDA',
        color: '#F87171',
        icon: 'Package',
        items: cogsItems,
        total: d?.cogsPendiente ? 'Parcial (pendiente)' : fmtEur(d?.totalCOGS || 0),
      },
      {
        title: 'MARGEN BRUTO',
        color: '#D6EF4E',
        icon: 'BarChart2',
        items: [],
        total: margenBrutoTotal,
        totalPct: margenBrutoPct,
        isResult: true,
      },
      {
        title: 'GASTOS DE PERSONAL',
        color: '#FBBF24',
        icon: 'Users',
        items: personalItems,
        total: fmtEur(d?.totalPersonal || 0),
      },
      {
        title: 'GASTOS OPERATIVOS',
        color: '#FBBF24',
        icon: 'Settings',
        items: [
          ...(d?.alquiler > 0 ? [{ label: 'Alquiler', value: fmtEur(d?.alquiler) }] : []),
          ...(d?.electricidad > 0 ? [{ label: 'Electricidad', value: fmtEur(d?.electricidad) }] : []),
          ...(d?.agua > 0 ? [{ label: 'Agua', value: fmtEur(d?.agua) }] : []),
          ...(d?.gestoria > 0 ? [{ label: 'Gestoría', value: fmtEur(d?.gestoria) }] : []),
          ...(d?.seguros > 0 ? [{ label: 'Seguros', value: fmtEur(d?.seguros) }] : []),
          ...(d?.software > 0 ? [{ label: 'Software y Telecomunicaciones', value: fmtEur(d?.software) }] : []),
          ...(d?.transporte > 0 ? [{ label: 'Transporte y Combustible', value: fmtEur(d?.transporte) }] : []),
          ...(d?.publicidad > 0 ? [{ label: 'Publicidad', value: fmtEur(d?.publicidad) }] : []),
          ...(d?.otrosGastos > 0 ? [{ label: 'Otros Gastos Operativos', value: fmtEur(d?.otrosGastos) }] : []),
        ],
        total: fmtEur(d?.totalGastosOperativos || 0),
      },
      {
        title: 'AMORTIZACIONES',
        color: '#A78BFA',
        icon: 'TrendingDown',
        items: [
          { label: 'Amortización Inmovilizado', value: 'Pendiente de datos', isPending: true, sub: '⚠ Módulo activos' },
        ],
        total: '—',
      },
      {
        title: 'RESULTADO OPERATIVO',
        color: (d?.resultadoOperativo || 0) >= 0 ? '#34D399' : '#F87171',
        icon: 'Target',
        items: [],
        total: fmtEur(d?.resultadoOperativo || 0),
        totalPct: d?.resultadoOperativoPct,
        isResult: true,
      },
      {
        title: 'GASTOS FINANCIEROS',
        color: '#F87171',
        icon: 'CreditCard',
        items: [
          { label: 'Comisiones Bancarias', value: fmtEur(d?.comisionesBancarias || 0) },
          { label: 'Intereses / Financiación', value: 'Pendiente de datos', isPending: true },
        ],
        total: fmtEur(d?.totalGastosFinancieros || 0),
      },
      {
        title: 'RESULTADO ANTES DE IMPUESTOS (ESTIMADO SIN AMORTIZACIONES)',
        color: '#60A5FA',
        icon: 'Calculator',
        items: [],
        total: fmtEur(d?.resultadoAnteImpuestos || 0),
        isResult: true,
      },
      {
        title: 'RESULTADO NETO FISCAL',
        color: '#A78BFA',
        icon: 'CheckCircle',
        items: [
          { label: 'Fiscalidad (IRPF / IS)', value: 'Pendiente de fase fiscal', isPending: true },
          { label: 'Amortizaciones', value: 'Pendiente de módulo activos', isPending: true },
        ],
        total: 'Pendiente de fiscalidad',
        isResult: true,
      },
    ];
  };

  const plSections = buildPLSections();

  // Comparativa: calcular diferencias
  const buildComparativaItems = () => {
    if (!comparativaData?.actual || !comparativaData?.anterior) return [];
    const a = comparativaData?.actual;
    const b = comparativaData?.anterior;
    const diff = (va, vb) => {
      const d = va - vb;
      const pct = vb !== 0 ? ((va - vb) / Math.abs(vb)) * 100 : 0;
      return { actual: va, anterior: vb, diff: d, pct };
    };
    return [
      { label: 'Ingresos Totales', data: diff(a?.totalIngresos || 0, b?.totalIngresos || 0), color: '#34D399' },
      { label: 'COGS', data: diff(a?.totalCOGS || 0, b?.totalCOGS || 0), color: '#F87171', invertSign: true },
      { label: 'Margen Bruto', data: diff(a?.margenBruto || 0, b?.margenBruto || 0), color: '#D6EF4E' },
      { label: 'Gastos Personal', data: diff(a?.totalPersonal || 0, b?.totalPersonal || 0), color: '#FBBF24', invertSign: true },
      { label: 'Gastos Operativos', data: diff(a?.totalGastosOperativos || 0, b?.totalGastosOperativos || 0), color: '#FBBF24', invertSign: true },
      { label: 'Resultado Operativo', data: diff(a?.resultadoOperativo || 0, b?.resultadoOperativo || 0), color: (a?.resultadoOperativo || 0) >= 0 ? '#34D399' : '#F87171' },
      { label: 'Gastos Financieros', data: diff(a?.totalGastosFinancieros || 0, b?.totalGastosFinancieros || 0), color: '#F87171', invertSign: true },
      { label: 'Resultado Antes Impuestos', data: diff(a?.resultadoAnteImpuestos || 0, b?.resultadoAnteImpuestos || 0), color: '#60A5FA' },
    ];
  };

  return (
    <div className="min-h-screen px-4 py-6 md:px-8"
      style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-5 text-xs font-oranienbaum text-white/30">
        <Icon name="Home" size={12} />
        <span>/</span>
        <span className="text-[#D6EF4E]/60">Finanzas Corporativas</span>
        <span>/</span>
        <span className="text-white/50">Contabilidad Analítica</span>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(214,239,78,0.10)', border: '1px solid rgba(214,239,78,0.25)' }}>
            <Icon name="BookOpen" size={20} className="text-[#D6EF4E]" />
          </div>
          <div>
            <h1 className="font-oranienbaum font-bold text-white text-2xl tracking-wide">Contabilidad Analítica</h1>
            <p className="font-oranienbaum text-white/40 text-xs">Motor económico consolidado · Mismo motor que el Dashboard Financiero</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={loadData} className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/60 hover:text-white transition-colors"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Icon name="RefreshCw" size={13} />
            Actualizar
          </button>
          <button className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/60 hover:text-white transition-colors"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Icon name="Download" size={13} />
            Exportar PDF
          </button>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="mb-5 p-3 rounded-xl flex items-start gap-3"
        style={{ background: 'rgba(251,191,36,0.04)', border: '1px solid rgba(251,191,36,0.15)' }}>
        <Icon name="AlertTriangle" size={15} className="text-[#FBBF24]/70 flex-shrink-0 mt-0.5" />
        <p className="font-oranienbaum text-white/40 text-xs leading-relaxed">
          <span className="text-[#FBBF24]/80 font-semibold">Contabilidad analítica de gestión.</span>{' '}
          No sustituye al programa oficial de contabilidad ni al asesor fiscal. Los datos son estimaciones de gestión interna.
          <span className="text-[#34D399]/60 ml-1">Conectada al mismo motor que el Dashboard Financiero.</span>
          <span className="text-[#FBBF24]/60 ml-1">Los valores "Pendiente de datos" indican fuentes no disponibles aún (Epelsa, Contasimple).</span>
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-xl flex items-center gap-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
          <Icon name="AlertCircle" size={15} className="text-red-400 flex-shrink-0" />
          <p className="font-oranienbaum text-red-400 text-xs">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-5">
        {/* Periodos */}
        <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {PERIODS?.map((p) => (
            <button key={p} onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all ${period === p ? 'text-black font-semibold' : 'text-white/50 hover:text-white/80'}`}
              style={period === p ? { background: '#D6EF4E' } : {}}>
              {p}
            </button>
          ))}
        </div>
        {period === 'Personalizado' && (
          <>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e?.target?.value)}
              className="px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none bg-transparent"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }} />
            <input type="date" value={dateTo} onChange={e => setDateTo(e?.target?.value)}
              className="px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none bg-transparent"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }} />
          </>
        )}
        {/* Departamentos */}
        <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {DEPTS?.map((d) => (
            <button key={d} onClick={() => setDept(d)}
              className={`px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all ${dept === d ? 'text-black font-semibold' : 'text-white/50 hover:text-white/80'}`}
              style={dept === d ? { background: '#D6EF4E' } : {}}>
              {d}
            </button>
          ))}
        </div>
      </div>

      {/* View Tabs */}
      <div className="flex gap-1 p-1 rounded-xl mb-6 w-fit"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
        {VIEWS?.map((v) => (
          <button key={v} onClick={() => setView(v)}
            className={`px-4 py-2 rounded-lg text-xs font-oranienbaum transition-all ${view === v ? 'text-black font-semibold' : 'text-white/50 hover:text-white/80'}`}
            style={view === v ? { background: '#D6EF4E' } : {}}>
            {v}
          </button>
        ))}
      </div>

      {/* ── CUENTA DE RESULTADOS ─────────────────────────────────────────── */}
      {view === 'Cuenta de Resultados' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 space-y-3">
            {loading && !data ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-6 h-6 rounded-full border-2 border-white/10 border-t-[#D6EF4E] animate-spin mr-3" />
                <span className="font-oranienbaum text-white/30 text-xs">Cargando contabilidad analítica...</span>
              </div>
            ) : (
              plSections?.map((section, i) => (
                <PLSection key={i} {...section} loading={loading} />
              ))
            )}
          </div>

          {/* Right panel */}
          <div className="space-y-4">
            {/* Cobertura de datos */}
            {data && <CoberturaIndicador data={data} />}

            {/* KPI resumen */}
            {data && (
              <div className="rounded-2xl p-4 space-y-3"
                style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="BarChart2" size={14} className="text-[#D6EF4E]" />
                  <h3 className="font-oranienbaum font-semibold text-white/70 text-sm">Resumen</h3>
                </div>
                {[
                  { label: 'Total Ingresos', value: fmtEur(data?.totalIngresos || 0), color: '#34D399' },
                  { label: 'Margen Bruto', value: data?.margenBrutoPendiente ? 'Parcial' : `${fmtEur(data?.margenBruto || 0)} (${fmtPct(data?.margenBrutoPct || 0)})`, color: '#D6EF4E' },
                  { label: 'Resultado Operativo', value: `${fmtEur(data?.resultadoOperativo || 0)} (${fmtPct(data?.resultadoOperativoPct || 0)})`, color: (data?.resultadoOperativo || 0) >= 0 ? '#34D399' : '#F87171' },
                  { label: 'Resultado Antes Impuestos', value: fmtEur(data?.resultadoAnteImpuestos || 0), color: '#60A5FA' },
                ]?.map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <span className="font-oranienbaum text-white/40 text-xs">{item?.label}</span>
                    <span className="font-oranienbaum font-semibold text-xs" style={{ color: item?.color }}>{item?.value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Fórmulas */}
            <div className="rounded-2xl p-4"
              style={{ background: 'rgba(255,255,255,0.015)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <div className="flex items-center gap-2 mb-3">
                <Icon name="Info" size={13} className="text-white/30" />
                <h3 className="font-oranienbaum text-white/40 text-xs">Fórmulas aplicadas</h3>
              </div>
              <div className="space-y-1.5 text-[10px] font-oranienbaum text-white/25">
                <p>Margen Bruto = Ingresos − COGS</p>
                <p>Resultado Operativo = MB − Personal − Operativos</p>
                <p>Resultado AI = RO − Gastos Financieros</p>
                <p>IVA: se usa base imponible (sin IVA) para gestión</p>
                <p>Recurrentes: imputación por periodicidad_imputacion</p>
              </div>
            </div>

            {/* Informes */}
            <div className="rounded-2xl p-4"
              style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex items-center gap-2 mb-3">
                <Icon name="FileBarChart" size={14} className="text-[#D6EF4E]" />
                <h3 className="font-oranienbaum font-semibold text-white/70 text-sm">Informes</h3>
              </div>
              <div className="space-y-1.5">
                {reports?.map((report, i) => (
                  <button key={i} className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-left hover:bg-white/5 transition-colors group"
                    style={{ border: '1px solid rgba(255,255,255,0.04)' }}>
                    <Icon name={report?.icon} size={13} style={{ color: report?.color }} />
                    <span className="font-oranienbaum text-white/50 text-xs group-hover:text-white/80 transition-colors flex-1">{report?.label}</span>
                    <Icon name="ChevronRight" size={11} className="text-white/20 group-hover:text-white/50 transition-colors" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── CENTROS DE COSTE ─────────────────────────────────────────────── */}
      {view === 'Centros de Coste' && (
        <div className="space-y-4">
          {/* Cobertura compacta */}
          {data && (
            <div className="flex items-center gap-3 px-4 py-2 rounded-xl"
              style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <Icon name="ShieldCheck" size={13} style={{ color: data?.coberturaPct >= 80 ? '#34D399' : '#FBBF24' }} />
              <span className="font-oranienbaum text-white/50 text-xs">Cobertura de datos:</span>
              <span className="font-oranienbaum font-bold text-xs" style={{ color: data?.coberturaPct >= 80 ? '#34D399' : '#FBBF24' }}>{data?.coberturaPct}%</span>
            </div>
          )}
          <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-6 h-6 rounded-full border-2 border-white/10 border-t-[#D6EF4E] animate-spin mr-3" />
                <span className="font-oranienbaum text-white/30 text-xs">Cargando centros de coste...</span>
              </div>
            ) : centrosData?.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-16">
                <Icon name="Layers" size={24} className="text-white/15" />
                <p className="font-oranienbaum text-white/30 text-sm">Sin centros de coste configurados</p>
                <p className="font-oranienbaum text-white/20 text-xs">Configure centros de coste en fin_centros_coste</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                    {['Centro de Coste', 'Tipo', 'Ingresos', 'Costes Directos', 'Costes Indirectos', 'Resultado', '% Gastos']?.map(col => (
                      <th key={col} className="px-4 py-3 text-left">
                        <span className="font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider">{col}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {centrosData?.map((c, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/2 transition-colors">
                      <td className="px-4 py-3"><span className="font-oranienbaum text-white/70 text-xs font-semibold">{c?.nombre}</span></td>
                      <td className="px-4 py-3">
                        <span className="font-oranienbaum text-[10px] px-2 py-0.5 rounded"
                          style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.40)' }}>
                          {c?.tipo || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right"><span className="font-oranienbaum text-[#34D399] text-xs">{fmtEur(c?.ingresos)}</span></td>
                      <td className="px-4 py-3 text-right"><span className="font-oranienbaum text-[#F87171] text-xs">{fmtEur(c?.costes)}</span></td>
                      <td className="px-4 py-3 text-right"><span className="font-oranienbaum text-white/30 text-xs">—</span></td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-oranienbaum text-xs font-semibold" style={{ color: c?.resultado >= 0 ? '#34D399' : '#F87171' }}>
                          {fmtEur(c?.resultado)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right"><span className="font-oranienbaum text-white/50 text-xs">{fmtPct(c?.pctSobreTotal)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── COMPARATIVA ──────────────────────────────────────────────────── */}
      {view === 'Comparativa' && (
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-6 h-6 rounded-full border-2 border-white/10 border-t-[#D6EF4E] animate-spin mr-3" />
              <span className="font-oranienbaum text-white/30 text-xs">Cargando comparativa...</span>
            </div>
          ) : comparativaData ? (
            <div className="space-y-4">
              {/* Header comparativa */}
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <Icon name="ArrowLeftRight" size={15} className="text-[#60A5FA]" />
                <h3 className="font-oranienbaum font-semibold text-white/70 text-sm">
                  Periodo Actual vs Periodo Anterior · {dept}
                </h3>
                <span className="font-oranienbaum text-white/30 text-xs ml-auto">Mismo motor contable</span>
              </div>

              {/* Tabla comparativa */}
              <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <table className="w-full">
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                      {['Concepto', 'Periodo Actual', 'Periodo Anterior', 'Variación €', 'Variación %']?.map(col => (
                        <th key={col} className="px-4 py-3 text-left">
                          <span className="font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider">{col}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {buildComparativaItems()?.map((item, i) => {
                      const isPositive = item?.invertSign ? item?.data?.diff <= 0 : item?.data?.diff >= 0;
                      const diffColor = isPositive ? '#34D399' : '#F87171';
                      return (
                        <tr key={i} className="border-b border-white/5 hover:bg-white/2 transition-colors">
                          <td className="px-4 py-3">
                            <span className="font-oranienbaum text-white/70 text-xs font-semibold">{item?.label}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-oranienbaum text-white/70 text-xs font-semibold" style={{ color: item?.color }}>
                              {fmtEur(item?.data?.actual)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-oranienbaum text-white/40 text-xs">{fmtEur(item?.data?.anterior)}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-oranienbaum text-xs font-semibold" style={{ color: diffColor }}>
                              {item?.data?.diff >= 0 ? '+' : ''}{fmtEur(item?.data?.diff)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Icon name={isPositive ? 'TrendingUp' : 'TrendingDown'} size={11} style={{ color: diffColor }} />
                              <span className="font-oranienbaum text-xs font-semibold" style={{ color: diffColor }}>
                                {item?.data?.pct >= 0 ? '+' : ''}{fmtPct(item?.data?.pct)}
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Nota cobertura */}
              {data && (
                <div className="flex items-center gap-3 px-4 py-2 rounded-xl"
                  style={{ background: 'rgba(251,191,36,0.04)', border: '1px solid rgba(251,191,36,0.10)' }}>
                  <Icon name="Info" size={13} className="text-[#FBBF24]/60" />
                  <span className="font-oranienbaum text-white/30 text-xs">
                    Cobertura actual: {data?.coberturaPct}% · Los valores "Pendiente" no se incluyen en la variación.
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 py-16">
              <Icon name="BarChart2" size={24} className="text-white/15" />
              <p className="font-oranienbaum text-white/30 text-sm">Sin datos de comparativa disponibles</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FinContabilidad;
